import { useEffect, useState, useCallback } from "react";
import api, { formatIDR, formatDate, formatApiError, buildWaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PaymentBadge, STATUS_OPTIONS, PAYMENT_OPTIONS, STATUS_META, PAYMENT_META } from "@/components/badges";
import Invoice from "@/components/Invoice";
import SlaughterNote from "@/components/SlaughterNote";
import KitchenNote from "@/components/KitchenNote";
import GreetingCard from "@/components/GreetingCard";
import Certificate from "@/components/Certificate";
import DeliveryNote from "@/components/DeliveryNote";
import { DateField } from "@/components/DateField";
import { TimeField } from "@/components/TimeField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, FileText, Search, MessageCircle, Download, Loader2, Baby, User, Users as UsersIcon, Truck, Beef, Video, Image as ImageIcon, ScrollText, ChefHat, Heart, Award, FileStack, CalendarDays, X } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const emptyOrder = {
  child_name: "", child_birth: "", child_gender: "laki-laki",
  father_name: "", father_phone: "", father_email: "",
  mother_name: "", mother_phone: "", mother_email: "",
  event_date: "", event_time: "", delivery_time: "", delivery_address: "", shipping_cost: "",
  package_id: null, lamb_type: "", lamb_sex: "jantan", lamb_condition: "dimasak", lamb_count: 1, unit_price: "",
  box_package_id: null, rice_box_type: "", box_count: "", box_price: "", sate_per_box: "",
  addons: [],
  slaughter_option: "", photo_url: "",
  notes: "", status: "baru", payment_status: "belum_bayar", down_payment: "", discount: "",
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl border border-brand-line p-4">
    <div className="mb-3 flex items-center gap-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-gold/15 text-brand-gold">
        <Icon className="h-4 w-4" />
      </div>
      <h3 className="font-serif text-lg font-bold text-brand-espresso">{title}</h3>
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-brand-muted">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const ring = "focus-visible:ring-brand-gold";

export default function Orders() {
  const { isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [eventStart, setEventStart] = useState("");
  const [eventEnd, setEventEnd] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOrder);
  const [busy, setBusy] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [slaughterOrder, setSlaughterOrder] = useState(null);
  const [kitchenOrder, setKitchenOrder] = useState(null);
  const [greetingOrder, setGreetingOrder] = useState(null);
  const [certificateOrder, setCertificateOrder] = useState(null);
  const [deliveryOrder, setDeliveryOrder] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (q) params.q = q;
    if (eventStart) params.event_start = eventStart;
    if (eventEnd) params.event_end = eventEnd;
    api.get("/orders", { params }).then(({ data }) => setOrders(data)).catch(() => {});
  }, [filter, q, eventStart, eventEnd]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/packages").then(({ data }) => setPackages(data.filter((p) => p.active))).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hewanPackages = packages.filter((p) => (p.category || "hewan") === "hewan")
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "id", { numeric: true }));
  const boxPackages = packages.filter((p) => p.category === "nasi_box");
  const addonPackages = packages.filter((p) => p.category === "menu_tambahan");

  const openNew = () => { setEditing(null); setForm(emptyOrder); setFormOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      ...emptyOrder,
      ...o,
      lamb_count: o.lamb_count ?? 1,
      unit_price: o.unit_price != null ? String(o.unit_price) : "",
      box_count: o.box_count != null ? String(o.box_count) : "",
      box_price: o.box_price != null ? String(o.box_price) : "",
      box_package_id: o.box_package_id || null,
      addons: o.addons || [],
      shipping_cost: o.shipping_cost != null ? String(o.shipping_cost) : "",
      down_payment: o.down_payment != null ? String(o.down_payment) : "",
      discount: o.discount != null ? String(o.discount) : "",
      event_date: (o.event_date || o.schedule_date || "").slice(0, 10),
    });
    setFormOpen(true);
  };

  const priceFor = (p, sex, condition) => {
    if (!p) return 0;
    if (condition === "mentah") {
      return sex === "betina" ? (p.price_female || p.price) : (p.price_male || p.price);
    }
    return sex === "betina"
      ? (p.price_female_cooked || p.price_female || p.price)
      : (p.price_male_cooked || p.price_male || p.price);
  };

  const onPickPackage = (pkgId) => {
    const p = hewanPackages.find((x) => x.id === pkgId);
    if (!p) return;
    setForm((f) => ({ ...f, package_id: p.id, lamb_type: p.name, unit_price: String(priceFor(p, f.lamb_sex, f.lamb_condition) || 0) }));
  };

  const onChangeSex = (sex) => {
    setForm((f) => {
      const p = hewanPackages.find((x) => x.id === f.package_id);
      if (!p) return { ...f, lamb_sex: sex };
      return { ...f, lamb_sex: sex, unit_price: String(priceFor(p, sex, f.lamb_condition) || 0) };
    });
  };

  const onChangeCondition = (condition) => {
    setForm((f) => {
      const p = hewanPackages.find((x) => x.id === f.package_id);
      if (!p) return { ...f, lamb_condition: condition };
      return { ...f, lamb_condition: condition, unit_price: String(priceFor(p, f.lamb_sex, condition) || 0) };
    });
  };

  const onPickBox = (pkgId) => {
    const p = boxPackages.find((x) => x.id === pkgId);
    if (!p) return;
    setForm((f) => ({ ...f, box_package_id: p.id, rice_box_type: p.name, box_price: String(p.price || 0) }));
  };

  const addAddon = (pkgId) => {
    const p = addonPackages.find((x) => x.id === pkgId);
    if (!p) return;
    setForm((f) => {
      if ((f.addons || []).some((a) => a.package_id === p.id)) return f;
      return { ...f, addons: [...(f.addons || []), { package_id: p.id, name: p.name, price: Number(p.price) || 0, qty: 1 }] };
    });
  };
  const setAddonQty = (idx, qty) => setForm((f) => {
    const arr = [...(f.addons || [])];
    arr[idx] = { ...arr[idx], qty: Math.max(1, Number(qty) || 1) };
    return { ...f, addons: arr };
  });
  const removeAddon = (idx) => setForm((f) => ({ ...f, addons: (f.addons || []).filter((_, i) => i !== idx) }));

  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (file.size > 2 * 1024 * 1024) return toast.error("Ukuran foto maksimal 2MB");
    const reader = new FileReader();
    reader.onload = () => set("photo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const lambTotal = (Number(form.unit_price) || 0) * (Number(form.lamb_count) || 1);
  const boxTotal = (Number(form.box_price) || 0) * (Number(form.box_count) || 0);
  const addonsTotal = (form.addons || []).reduce((s, a) => s + (Number(a.price) || 0) * (Number(a.qty) || 1), 0);
  const shippingTotal = Number(form.shipping_cost) || 0;
  const discount = Number(form.discount) || 0;
  const total = Math.max(0, lambTotal + boxTotal + addonsTotal + shippingTotal - discount);

  const save = async () => {
    if (!form.child_name.trim()) return toast.error("Nama anak wajib diisi");
    if (!form.father_name.trim() && !form.mother_name.trim()) return toast.error("Isi minimal nama ayah atau ibu");
    setBusy(true);
    try {
      const payload = {
        ...form,
        lamb_count: Number(form.lamb_count) || 1,
        unit_price: Number(form.unit_price) || 0,
        box_count: Number(form.box_count) || 0,
        box_price: Number(form.box_price) || 0,
        addons: (form.addons || []).map((a) => ({ package_id: a.package_id, name: a.name, price: Number(a.price) || 0, qty: Number(a.qty) || 1 })),
        shipping_cost: Number(form.shipping_cost) || 0,
        discount: Number(form.discount) || 0,
        down_payment: Number(form.down_payment) || 0,
      };
      if (editing) {
        await api.put(`/orders/${editing.id}`, payload);
        toast.success("Pesanan diperbarui");
      } else {
        await api.post("/orders", payload);
        toast.success("Pesanan dibuat");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const quickUpdate = async (id, fields) => {
    try {
      await api.patch(`/orders/${id}/status`, fields);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Pesanan dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (startDate) params.start = startDate;
      if (endDate) params.end = endDate;
      const res = await api.get("/orders/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-rizqia-aqiqah${startDate ? "_" + startDate : ""}${endDate ? "_" + endDate : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Laporan berhasil diunduh");
      setExportOpen(false);
    } catch {
      toast.error("Gagal mengunduh laporan");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="animate-fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Operasional</p>
          <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Pesanan</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-orders-btn"
            className="gap-2 border-brand-line text-brand-espresso hover:bg-brand-surface transition-colors duration-200">
            <Download className="h-4 w-4" /> Ekspor Excel
          </Button>
          <Button onClick={openNew} data-testid="add-order-btn"
            className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover transition-colors duration-200">
            <Plus className="h-4 w-4" /> Buat Pesanan
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama anak / ayah / no. pesanan..."
            data-testid="order-search" className={`pl-9 ${ring}`} />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 rounded-md border border-brand-line px-3 py-1.5">
          <CalendarDays className="h-4 w-4 text-brand-muted" />
          <span className="text-xs text-brand-muted">Jadwal:</span>
          <DateField value={eventStart} onChange={setEventStart} hideIcon testid="order-event-start" className="h-8 w-40 border-0 bg-transparent px-0 shadow-none" placeholder="Dari tgl" />
          <span className="text-brand-muted">–</span>
          <DateField value={eventEnd} onChange={setEventEnd} hideIcon testid="order-event-end" className="h-8 w-40 border-0 bg-transparent px-0 shadow-none" placeholder="Sampai tgl" />
          {(eventStart || eventEnd) && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEventStart(""); setEventEnd(""); }}
              data-testid="order-event-clear" title="Hapus filter tanggal">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-line bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
              <th className="px-4 py-3 font-semibold">No. / Anak</th>
              <th className="px-4 py-3 font-semibold">Orang Tua</th>
              <th className="px-4 py-3 font-semibold">Jadwal</th>
              <th className="px-4 py-3 font-semibold">Foto</th>
              <th className="px-4 py-3 font-semibold">Bayar</th>
              <th className="px-4 py-3 text-right font-semibold">Total</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 text-right font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-brand-muted">Belum ada pesanan.</td></tr>
            ) : (
              orders.map((o, i) => (
                <motion.tr key={o.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                  className="border-b border-brand-line/60 hover:bg-brand-surface/40" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-brand-espresso">{o.child_name || o.aqiqah_name || "-"}</p>
                    <p className="text-xs text-brand-muted">{o.order_number}</p>
                  </td>
                  <td className="px-4 py-3 text-brand-muted">
                    <p className="text-brand-espresso">{o.father_name || o.customer_name || "-"}</p>
                    <p className="text-xs">{o.father_phone || o.customer_phone || ""}</p>
                  </td>
                  <td className="px-4 py-3 text-brand-muted">
                    {formatDate(o.event_date || o.schedule_date)}
                    {o.event_time ? <span className="block text-xs">{o.event_time}</span> : null}
                  </td>
                  <td className="px-4 py-3">
                    {o.photo_url ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800" data-testid={`photo-status-${o.id}`}>
                        <ImageIcon className="h-3 w-3" /> Ada
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600" data-testid={`photo-status-${o.id}`}>
                        Belum
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select value={o.payment_status} onValueChange={(v) => quickUpdate(o.id, { payment_status: v })}>
                      <SelectTrigger className="h-8 w-[120px] border-none bg-transparent px-1 shadow-none focus:ring-0" data-testid={`order-payment-${o.id}`}>
                        <PaymentBadge status={o.payment_status} />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_OPTIONS.map((s) => <SelectItem key={s} value={s}>{PAYMENT_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-brand-espresso">{formatIDR(o.total_amount)}</td>
                  <td className="px-4 py-3">
                    <Select value={o.status} onValueChange={(v) => quickUpdate(o.id, { status: v })}>
                      <SelectTrigger className="h-8 w-[135px] border-none bg-transparent px-1 shadow-none focus:ring-0" data-testid={`order-status-${o.id}`}>
                        <StatusBadge status={o.status} />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`docs-order-${o.id}`} title="Dokumen">
                            <FileStack className="h-4 w-4 text-brand-gold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuLabel>Dokumen</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setInvoiceOrder(o)} data-testid={`invoice-order-${o.id}`}>
                            <FileText className="mr-2 h-4 w-4 text-brand-gold" /> Kuitansi
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSlaughterOrder(o)} data-testid={`slaughter-order-${o.id}`}>
                            <ScrollText className="mr-2 h-4 w-4 text-brand-espresso" /> Form Sembelih
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setKitchenOrder(o)} data-testid={`kitchen-order-${o.id}`}>
                            <ChefHat className="mr-2 h-4 w-4 text-brand-gold" /> Form Dapur
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeliveryOrder(o)} data-testid={`delivery-order-${o.id}`}>
                            <Truck className="mr-2 h-4 w-4 text-brand-espresso" /> Surat Jalan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setGreetingOrder(o)} data-testid={`greeting-order-${o.id}`}>
                            <Heart className="mr-2 h-4 w-4 text-brand-espresso" /> Kartu Ucapan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCertificateOrder(o)} data-testid={`certificate-order-${o.id}`}>
                            <Award className="mr-2 h-4 w-4 text-brand-gold" /> Sertifikat Aqiqah
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(o)} data-testid={`edit-order-${o.id}`} title="Ubah">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-red-50" data-testid={`delete-order-${o.id}`} title="Hapus">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus pesanan?</AlertDialogTitle>
                              <AlertDialogDescription>Pesanan {o.order_number} akan dihapus permanen.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(o.id)} className="bg-destructive hover:bg-destructive/90">Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Ubah Pesanan" : "Buat Pesanan"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Data anak */}
            <Section icon={Baby} title="Data yang Diaqiqahi">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="Nama Lengkap Anak *">
                  <Input value={form.child_name} onChange={(e) => set("child_name", e.target.value)} data-testid="order-child-name" className={ring} />
                </Field>
                <Field label="Tanggal Lahir">
                  <Input value={form.child_birth} onChange={(e) => set("child_birth", e.target.value)} placeholder="12 Januari 2026" data-testid="order-child-birth" className={ring} />
                </Field>
                <Field label="Jenis Kelamin">
                  <Select value={form.child_gender} onValueChange={(v) => set("child_gender", v)}>
                    <SelectTrigger data-testid="order-child-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laki-laki">Laki-laki</SelectItem>
                      <SelectItem value="perempuan">Perempuan</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Section>

            {/* Ayah & Ibu */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Section icon={User} title="Data Ayah">
                <div className="space-y-3">
                  <Field label="Nama Lengkap Ayah">
                    <Input value={form.father_name} onChange={(e) => set("father_name", e.target.value)} data-testid="order-father-name" className={ring} />
                  </Field>
                  <Field label="No. HP">
                    <Input value={form.father_phone} onChange={(e) => set("father_phone", e.target.value)} placeholder="0812..." data-testid="order-father-phone" className={ring} />
                  </Field>
                  <Field label="E-mail">
                    <Input type="email" value={form.father_email} onChange={(e) => set("father_email", e.target.value)} data-testid="order-father-email" className={ring} />
                  </Field>
                </div>
              </Section>
              <Section icon={UsersIcon} title="Data Ibu">
                <div className="space-y-3">
                  <Field label="Nama Lengkap Ibu">
                    <Input value={form.mother_name} onChange={(e) => set("mother_name", e.target.value)} data-testid="order-mother-name" className={ring} />
                  </Field>
                  <Field label="No. HP">
                    <Input value={form.mother_phone} onChange={(e) => set("mother_phone", e.target.value)} placeholder="0812..." data-testid="order-mother-phone" className={ring} />
                  </Field>
                  <Field label="E-mail">
                    <Input type="email" value={form.mother_email} onChange={(e) => set("mother_email", e.target.value)} data-testid="order-mother-email" className={ring} />
                  </Field>
                </div>
              </Section>
            </div>

            {/* Pengiriman */}
            <Section icon={Truck} title="Data Pengiriman Aqiqah">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="Hari/Tanggal Aqiqah">
                  <DateField value={form.event_date} onChange={(v) => set("event_date", v)} testid="order-event-date" />
                </Field>
                <Field label="Jam Acara Aqiqah">
                  <TimeField value={form.event_time} onChange={(v) => set("event_time", v)} testid="order-event-time" />
                </Field>
                <Field label="Jam Tiba Kiriman">
                  <TimeField value={form.delivery_time} onChange={(v) => set("delivery_time", v)} testid="order-delivery-time" />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Alamat Lengkap">
                  <Textarea value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} data-testid="order-address" className={ring} />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Ongkos Kirim (Rp)">
                  <Input type="number" value={form.shipping_cost} onChange={(e) => set("shipping_cost", e.target.value)} data-testid="order-shipping-cost" className={ring} />
                </Field>
              </div>
            </Section>

            {/* Pesanan */}
            <Section icon={Beef} title="Data Pesanan Aqiqah">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Field label="Tipe Domba/Kambing">
                  <Select value={form.package_id || ""} onValueChange={onPickPackage}>
                    <SelectTrigger data-testid="order-lamb-type"><SelectValue placeholder="Pilih paket" /></SelectTrigger>
                    <SelectContent>
                      {hewanPackages.length === 0 && <div className="px-3 py-2 text-sm text-brand-muted">Tidak ada paket aktif</div>}
                      {hewanPackages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.lamb_type && !form.package_id && <p className="mt-1 text-xs text-brand-muted">{form.lamb_type}</p>}
                </Field>
                <Field label="Jantan / Betina">
                  <Select value={form.lamb_sex} onValueChange={onChangeSex}>
                    <SelectTrigger data-testid="order-lamb-sex"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jantan">Jantan</SelectItem>
                      <SelectItem value="betina">Betina</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kondisi">
                  <Select value={form.lamb_condition} onValueChange={onChangeCondition}>
                    <SelectTrigger data-testid="order-lamb-condition"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dimasak">Sudah Dimasak</SelectItem>
                      <SelectItem value="mentah">Mentah</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Jumlah Ekor">
                  <Input type="number" min="1" value={form.lamb_count} onChange={(e) => set("lamb_count", e.target.value)} data-testid="order-lamb-count" className={ring} />
                </Field>
                <Field label="Harga per Ekor (Rp)">
                  <Input type="number" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} data-testid="order-unit-price" className={ring} />
                </Field>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="Tipe Nasi Box">
                  <Select value={form.box_package_id || ""} onValueChange={onPickBox}>
                    <SelectTrigger data-testid="order-rice-box-type"><SelectValue placeholder="Pilih nasi box" /></SelectTrigger>
                    <SelectContent>
                      {boxPackages.length === 0 && <div className="px-3 py-2 text-sm text-brand-muted">Belum ada nasi box</div>}
                      {boxPackages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.rice_box_type && !form.box_package_id && <p className="mt-1 text-xs text-brand-muted">{form.rice_box_type}</p>}
                </Field>
                <Field label="Jumlah Box">
                  <Input type="number" value={form.box_count} onChange={(e) => set("box_count", e.target.value)} data-testid="order-box-count" className={ring} />
                </Field>
                <Field label="Harga per Box (Rp)">
                  <Input type="number" value={form.box_price} onChange={(e) => set("box_price", e.target.value)} data-testid="order-box-price" className={ring} />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Add On">
                  <Select value="" onValueChange={addAddon}>
                    <SelectTrigger data-testid="order-addon-select"><SelectValue placeholder="Tambah add on" /></SelectTrigger>
                    <SelectContent>
                      {addonPackages.length === 0 && <div className="px-3 py-2 text-sm text-brand-muted">Belum ada add on</div>}
                      {addonPackages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {(form.addons || []).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {form.addons.map((a, idx) => (
                      <div key={a.package_id || idx} className="flex items-center gap-2 rounded-lg border border-brand-line p-2" data-testid={`order-addon-item-${idx}`}>
                        <span className="flex-1 text-sm text-brand-espresso">{a.name}</span>
                        <span className="text-xs text-brand-muted">{formatIDR(a.price)}</span>
                        <Input type="number" min="1" value={a.qty} onChange={(e) => setAddonQty(idx, e.target.value)} data-testid={`order-addon-qty-${idx}`} className="h-8 w-16" />
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeAddon(idx)} data-testid={`order-addon-remove-${idx}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3">
                <Field label="Diskon (Rp)">
                  <Input type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} data-testid="order-discount" className={ring} />
                </Field>
              </div>
              <div className="mt-4 space-y-1 border-t border-brand-line pt-3 text-sm">
                <div className="flex justify-between text-brand-muted">
                  <span>Domba/Kambing ({Number(form.lamb_count) || 1} ekor)</span>
                  <span>{formatIDR(lambTotal)}</span>
                </div>
                {boxTotal > 0 && (
                  <div className="flex justify-between text-brand-muted">
                    <span>Nasi Box ({Number(form.box_count) || 0} box)</span>
                    <span>{formatIDR(boxTotal)}</span>
                  </div>
                )}
                {addonsTotal > 0 && (
                  <div className="flex justify-between text-brand-muted">
                    <span>Add On</span>
                    <span>{formatIDR(addonsTotal)}</span>
                  </div>
                )}
                {shippingTotal > 0 && (
                  <div className="flex justify-between text-brand-muted">
                    <span>Ongkos Kirim</span>
                    <span>{formatIDR(shippingTotal)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-brand-muted">
                    <span>Diskon</span>
                    <span>- {formatIDR(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1 font-serif text-lg font-bold text-brand-espresso">
                  <span>Total</span>
                  <span className="text-brand-gold" data-testid="order-form-total">{formatIDR(total)}</span>
                </div>
              </div>
            </Section>

            {/* Penyembelihan */}
            <Section icon={Video} title="Penyembelihan">
              <Field label="Pilihan Penyembelihan">
                <Select value={form.slaughter_option || "none"} onValueChange={(v) => set("slaughter_option", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="order-slaughter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tidak diisi —</SelectItem>
                    <SelectItem value="disaksikan">Disaksikan Langsung</SelectItem>
                    <SelectItem value="video_call">Video Call</SelectItem>
                    <SelectItem value="dokumentasi">Dokumentasi</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </Section>

            {/* Foto */}
            <Section icon={ImageIcon} title="Foto untuk Kartu Ucapan & Sertifikat">
              <div className="flex items-center gap-4">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="Foto anak" className="h-24 w-24 rounded-lg border border-brand-line object-cover" data-testid="order-photo-preview" />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border border-dashed border-brand-line text-brand-muted">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-md border border-brand-line px-3 py-2 text-sm text-brand-espresso hover:bg-brand-surface" data-testid="order-photo-upload">
                    <ImageIcon className="h-4 w-4" /> {form.photo_url ? "Ganti Foto" : "Unggah Foto"}
                    <input type="file" accept="image/*" className="hidden" onChange={onPhotoChange} data-testid="order-photo-input" />
                  </label>
                  {form.photo_url && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => set("photo_url", "")} className="w-fit gap-1 text-destructive" data-testid="order-photo-remove">
                      <Trash2 className="h-3.5 w-3.5" /> Hapus Foto
                    </Button>
                  )}
                  <p className="text-xs text-brand-muted">Format gambar, maks 2MB. Dipakai untuk kartu ucapan & sertifikat aqiqah.</p>
                </div>
              </div>
            </Section>

            {/* Status & pembayaran */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Field label="Status Pesanan">
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="order-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status Pembayaran">
                <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                  <SelectTrigger data-testid="order-form-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_OPTIONS.map((s) => <SelectItem key={s} value={s}>{PAYMENT_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Uang Muka / DP (Rp)">
                <Input type="number" value={form.down_payment} onChange={(e) => set("down_payment", e.target.value)} data-testid="order-dp" className={ring} />
              </Field>
            </div>

            <Field label="Catatan">
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="order-notes" className={ring} />
            </Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy} data-testid="save-order-btn"
              className="bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">Simpan Pesanan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Ekspor Laporan (Excel)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-brand-muted">
            Pilih rentang tanggal berdasarkan tanggal pesanan dibuat. Kosongkan untuk mengekspor semua data.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Dari Tanggal</Label>
              <DateField value={startDate} onChange={setStartDate} testid="export-start-date" className="mt-1.5" />
            </div>
            <div>
              <Label>Sampai Tanggal</Label>
              <DateField value={endDate} onChange={setEndDate} testid="export-end-date" className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Batal</Button>
            <Button onClick={doExport} disabled={exporting} data-testid="download-export-btn"
              className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Unduh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Invoice order={invoiceOrder} open={!!invoiceOrder} onClose={() => setInvoiceOrder(null)} />
      <SlaughterNote order={slaughterOrder} open={!!slaughterOrder} onClose={() => setSlaughterOrder(null)} />
      <KitchenNote order={kitchenOrder} open={!!kitchenOrder} onClose={() => setKitchenOrder(null)} />
      <GreetingCard order={greetingOrder} open={!!greetingOrder} onClose={() => setGreetingOrder(null)} />
      <Certificate order={certificateOrder} open={!!certificateOrder} onClose={() => setCertificateOrder(null)} />
      <DeliveryNote order={deliveryOrder} open={!!deliveryOrder} onClose={() => setDeliveryOrder(null)} />
    </div>
  );
}
